import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import {
  LogOut, Play, Calendar, Tv2, User, Loader2, Radio,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { liveApi, getViewerMeta, clearViewer, getViewerToken, clearAppCaches } from './liveClient';

export default function LiveDashboardPage() {
  const nav = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewer = getViewerMeta() || {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await liveApi().get('/livestream/viewer/my-streams');
      setStreams(r.data?.streams || []);
    } catch (e) {
      if (e?._kicked) { toast.error(e._friendly); setTimeout(() => nav('/live/login', { replace: true }), 1500); }
      else if (e?.response?.status === 401) nav('/live/login', { replace: true });
      else toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => {
    if (!getViewerToken()) { nav('/live/login', { replace: true }); return; }
    // Clear stale cached assets on entering the dashboard so deploys don't cause UI issues.
    clearAppCaches();
    load();
  }, [load, nav]);

  useEffect(() => {
    const t = setInterval(async () => {
      try { await liveApi().post('/livestream/viewer/heartbeat'); }
      catch (e) {
        if (e?._kicked) {
          toast.error(e._friendly || 'Logged in from another device');
          clearViewer(); setTimeout(() => nav('/live/login', { replace: true }), 1500);
        }
      }
    }, 30000);
    return () => clearInterval(t);
  }, [nav]);

  const logout = async () => {
    try { await liveApi().post('/livestream/viewer/logout'); } catch { /* ignore */ }
    clearViewer(); nav('/live/login', { replace: true });
  };

  const liveCount = streams.filter((s) => s.status === 'live').length;

  return (
    <div className="min-h-screen relative overflow-hidden" onContextMenu={(e) => e.preventDefault()} style={{
      background: 'radial-gradient(ellipse at top, #fff8ec 0%, #fdf3df 45%, #f7ead0 100%)',
    }}>
      <Toaster richColors position="top-center" />

      {/* Subtle gold ornamental pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60'><path d='M30 6 l8 22 22 2 -18 14 8 22 -20-14 -20 14 8-22 -18-14 22-2z' fill='none' stroke='%23a16207' stroke-width='0.6'/></svg>\")",
        backgroundSize: '60px 60px',
      }} />

      {/* Header */}
      <header className="relative border-b border-amber-200/70 backdrop-blur-sm" style={{
        background: 'linear-gradient(180deg, rgba(255, 251, 240, 0.92) 0%, rgba(253, 243, 223, 0.6) 100%)',
      }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-amber-300 blur-md opacity-40 rounded-full" />
              <Radio className="relative w-6 h-6 text-amber-700" />
            </div>
            <div>
              <div className="text-amber-900 font-bold tracking-wide text-sm sm:text-base">SMNGP <span className="text-amber-700">Live</span></div>
              <div className="text-amber-800/60 text-[10px] uppercase tracking-[0.2em] hidden sm:block">Anjuman e Saifee</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-300/70 bg-white/70 text-sm">
              <User className="w-3.5 h-3.5 text-amber-700" />
              <span className="text-amber-900 font-medium">{viewer.name || `ITS ${viewer.its_id}`}</span>
            </div>
            <Button size="sm" variant="ghost" onClick={logout} className="text-amber-800 hover:text-amber-900 hover:bg-amber-100" data-testid="live-logout-btn">
              <LogOut className="w-4 h-4 mr-1" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Hero — Alam with Ya Hussain calligraphy */}
      <section className="relative max-w-6xl mx-auto px-4 pt-8 pb-4 sm:pt-10 sm:pb-6 text-center">
        <div className="relative inline-block">
          {/* Soft gold halo */}
          <div className="absolute inset-0 blur-3xl opacity-50" style={{ background: 'radial-gradient(circle, rgba(217, 119, 6, 0.35) 0%, transparent 70%)' }} />
          <img
            src="/live/alam.png"
            alt="Ya Hussain Alam"
            className="relative w-56 sm:w-72 md:w-80 mx-auto drop-shadow-[0_8_24px_rgba(120,53,15,0.25)]"
            style={{ animation: 'wave 6s ease-in-out infinite' }}
          />
        </div>

        <div className="mt-6 sm:mt-8 space-y-1">
          <div className="text-amber-700/90 text-xs sm:text-sm tracking-[0.4em] uppercase font-light">Labbaik Ya Hussain</div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(180deg, #b45309 0%, #92400e 50%, #78350f 100%)',
            fontFamily: '"Playfair Display", "Cormorant Garamond", serif',
            letterSpacing: '0.02em',
          }}>
            1448H ASHARA MUBARAK
          </h1>
          <div className="flex items-center justify-center gap-3 pt-2">
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-amber-600/60 to-transparent" />
            <span className="text-amber-800/80 text-xs tracking-[0.3em] uppercase">Anjuman E Saifee, Nagpur</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent via-amber-600/60 to-transparent" />
          </div>
        </div>

        {liveCount > 0 && (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-300 shadow-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
            </span>
            <span className="text-red-700 text-sm font-semibold tracking-wide">{liveCount} stream{liveCount > 1 ? 's' : ''} live now</span>
          </div>
        )}
      </section>

      {/* Streams */}
      <main className="relative max-w-6xl mx-auto px-4 pb-12">
        <div className="flex items-center justify-between mb-4 sm:mb-6 mt-4">
          <h2 className="text-amber-900 font-semibold text-lg tracking-wide flex items-center gap-2">
            <span className="h-5 w-1 bg-amber-600 rounded" />
            My Streams
          </h2>
          <span className="text-amber-800/60 text-xs">{streams.length} authorised</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-700" /></div>
        ) : streams.length === 0 ? (
          <div className="rounded-xl p-12 text-center bg-white/70 border border-amber-200 shadow-sm">
            <Tv2 className="w-12 h-12 text-amber-500/60 mx-auto mb-3" />
            <h3 className="font-semibold text-lg text-amber-900">No streams assigned</h3>
            <p className="text-amber-800/70 text-sm mt-2 max-w-md mx-auto">You haven't been granted access to any stream yet.<br />Please contact the administrator.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {streams.map((s) => (
              <StreamCard key={s.id} s={s} onWatch={() => nav(`/live/watch/${s.id}`)} />
            ))}
          </div>
        )}
      </main>

      <footer className="relative max-w-6xl mx-auto px-4 py-6 text-center">
        <div className="text-amber-800/40 text-[11px] tracking-[0.3em] uppercase">Nagpur</div>
      </footer>

      <style>{`
        @keyframes wave {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50% { transform: rotate(2deg) translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

const StreamCard = ({ s, onWatch }) => {
  const isLive = s.status === 'live';
  return (
    <button
      onClick={onWatch}
      disabled={s.status === 'draft'}
      className="group text-left rounded-xl overflow-hidden transition-all hover:scale-[1.015] disabled:opacity-50 disabled:cursor-not-allowed bg-white shadow-md hover:shadow-xl"
      style={{
        border: `1px solid ${isLive ? 'rgb(252, 165, 165)' : 'rgb(252, 211, 77)'}`,
        boxShadow: isLive ? '0 0 22px rgba(220, 38, 38, 0.12)' : '0 4px 16px rgba(120, 53, 15, 0.08)',
      }}
      data-testid={`viewer-stream-${s.id}`}
    >
      <div className="relative aspect-video bg-amber-50 overflow-hidden">
        {s.banner_url || s.thumbnail_url ? (
          <img src={s.banner_url || s.thumbnail_url} alt={s.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          }}>
            <img src="/live/alam.png" alt="" className="h-36 opacity-70" />
          </div>
        )}
        {/* Status badge */}
        <div className="absolute top-3 left-3">
          {isLive ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] font-bold tracking-wider uppercase shadow-md">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              Live
            </span>
          ) : s.status === 'scheduled' ? (
            <span className="px-2.5 py-1 rounded-full bg-amber-600 text-white text-[11px] font-bold tracking-wider uppercase shadow-md">Scheduled</span>
          ) : (
            <span className="px-2.5 py-1 rounded-full bg-slate-500 text-white text-[11px] font-bold tracking-wider uppercase shadow-md">{s.status}</span>
          )}
        </div>
        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-amber-900/15">
          <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg" style={{
            background: 'radial-gradient(circle, #fbbf24 0%, #b45309 100%)',
          }}>
            <Play className="w-6 h-6 text-white ml-0.5" fill="currentColor" />
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-bold text-lg leading-tight text-amber-900 group-hover:text-amber-700 transition-colors" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{s.title}</h3>
        {s.description && <p className="text-sm text-amber-800/70 mt-1.5 line-clamp-2">{s.description}</p>}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-200/70">
          <div className="text-xs text-amber-800/70 flex items-center gap-1">
            {s.stream_date ? <><Calendar className="w-3 h-3" />{s.stream_date}{s.start_time ? ` · ${s.start_time}` : ''}</> : s.category || '—'}
          </div>
          <span className="text-amber-700 text-xs font-semibold tracking-wider uppercase group-hover:translate-x-1 transition-transform">Watch →</span>
        </div>
      </div>
    </button>
  );
};
